function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { CssContainer } from "../../components/container";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { hasTenantFeature } from "../../functions/tenant-features";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { fetchSales, titleCase, resolveBusinessModel, rangeLabelFor, RANGES } from "../sales/territory-common";
import { lapseKey } from "../sales/sales-api";
import PeopleShell, { PEOPLE_SUBTITLES } from "../people/shell";
import { getCustomers } from "../people/api";
import CustomerModal from "./customer-modal";

/*
 * Customers list (People redesign 2026-10-09, board "3 · Customers").
 *
 * Data: GET /v1/people/customers (items, count, counts, summary). The pill counts come from the
 * same response as the rows, so they always agree. When that endpoint is not deployed yet
 * (getCustomers returns null) the list falls back to GET /v1/lifecycle/customers in the same layout:
 * no summary card, and only the selected pill carries a count.
 *
 * Money: sales figures are the workspace's own sales, so they show for every tenant. Ad spend is the
 * only thing managed tenants must never see, and nothing on this page shows it.
 */

const PAGE = 50;
const CHANNELS = [["", "All channels"], ["b2b", "Business"], ["b2c", "Consumer"]];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── shared helpers (also used by detail.jsx) ───────────────────────────────

export function g(o, key) {
  if (!o) return undefined;
  if (o[key] !== undefined) return o[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return o[pascal];
}
export const n = v => Number(v) || 0;
export const count = v => n(v).toLocaleString("en-US");

/** Whole dollars ("$14,280"); compact above a million ("$1.24M") when compact is set. */
export function money(v, compact) {
  const x = n(v);
  if (compact && Math.abs(x) >= 1e6) return `$${(x / 1e6).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (compact && Math.abs(x) >= 1e5) return `$${Math.round(x / 1e3).toLocaleString("en-US")}k`;
  return "$" + Math.round(x).toLocaleString("en-US");
}
function parseDate(d) {
  if (!d) return null;
  const x = new Date(d);
  return isNaN(x.getTime()) ? null : x;
}

/** "Sep 22" this year, "Sep 22, 2025" otherwise. */
export function shortDate(d) {
  const x = parseDate(d);
  if (!x) return "";
  const s = `${MONTHS[x.getMonth()]} ${x.getDate()}`;
  return x.getFullYear() === new Date().getFullYear() ? s : `${s}, ${x.getFullYear()}`;
}

/** "Feb 2026". */
export function monthYear(d) {
  const x = parseDate(d);
  return x ? `${MONTHS[x.getMonth()]} ${x.getFullYear()}` : "";
}

/** "2026-05" to "May" without timezone drift. */
export function monthLabel(ym) {
  const m = /^(\d{4})-(\d{1,2})/.exec(String(ym || ""));
  return m ? MONTHS[Number(m[2]) - 1] || "" : "";
}
export const plural = (k, one, many) => `${count(k)} ${n(k) === 1 ? one : many}`;
export const STAGE_LABEL = {
  lead: "Lead",
  prospect: "Prospect",
  customer: "Customer"
};
export const HEALTH_LABEL = {
  healthy: "Healthy",
  attention: "Needs attention",
  new: "New"
};
export function StageChip({
  stage,
  customerWord = "Customer"
}) {
  const s = String(stage || "").toLowerCase();
  if (!STAGE_LABEL[s]) return null;
  return /*#__PURE__*/React.createElement("span", {
    className: `pcu-chip pcu-st-${s}`
  }, s === "customer" ? customerWord : STAGE_LABEL[s]);
}
export function HealthChip({
  health
}) {
  const h = String(health || "").toLowerCase();
  if (!HEALTH_LABEL[h]) return null;
  return /*#__PURE__*/React.createElement("span", {
    className: `pcu-chip pcu-h-${h}`
  }, HEALTH_LABEL[h]);
}

/** Legacy lapse model (healthy | watch | at_risk | lapsing) to the new health words. */
export function legacyHealth(row) {
  const k = lapseKey(g(row, "lapseRisk"));
  if (!k) return null;
  return k === "healthy" ? "healthy" : "attention";
}

/** Wholesale workspaces call customers "Dealers" (unchanged businessModel rule). */
export const nounsFor = bm => bm === "retail" ? {
  title: "Customers",
  one: "customer",
  many: "customers",
  word: "Customer"
} : {
  title: "Dealers",
  one: "dealer",
  many: "dealers",
  word: "Dealer"
};

// ── list ───────────────────────────────────────────────────────────────────

function normalizeLegacy(r) {
  return {
    id: g(r, "id") != null ? String(g(r, "id")) : "",
    name: g(r, "name") && !String(g(r, "name")).includes("@") ? titleCase(g(r, "name")) : "",
    email: g(r, "email") || "",
    customerNumber: g(r, "customerNumber") || "",
    city: g(r, "shipToCity") || g(r, "billToCity") || "",
    state: g(r, "shipToRegion") || g(r, "billToRegion") || "",
    people: null,
    stage: String(g(r, "lifecycleStage") || "").toLowerCase(),
    orders: null,
    salesYtd: null,
    lastOrder: null,
    health: legacyHealth(r),
    legacy: true
  };
}
function normalize(r) {
  return {
    ...r,
    id: r.id != null ? String(r.id) : "",
    stage: String(r.stage || "").toLowerCase(),
    health: r.health ? String(r.health).toLowerCase() : null
  };
}

// ?q= opens the list already searched (the toolbar search's list link).
function urlQ() {
  try {
    return new URLSearchParams(window.location.search).get("q") || "";
  } catch (e) {
    return "";
  }
}
class CustomersViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "reload", () => {
      this.setState({
        rows: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "loadMore", async () => {
      if (this.state.loadingMore) return;
      const seq = this._seq;
      this.setState({
        loadingMore: true
      });
      try {
        const page = await this.fetchPage(this.state.rows.length);
        if (!this._alive || seq !== this._seq) return;
        this.setState({
          rows: [...this.state.rows, ...page.rows],
          loadingMore: false
        });
      } catch (e) {
        if (this._alive) this.setState({
          loadingMore: false
        });
      }
    });
    _defineProperty(this, "onFilter", filter => () => {
      if (filter !== this.state.filter) this.setState({
        filter
      }, this.reload);
    });
    _defineProperty(this, "onChannel", channel => this.setState({
      channel,
      summary: null
    }, this.reload));
    _defineProperty(this, "onSearch", e => {
      const q = e.target.value;
      this.setState({
        q
      });
      clearTimeout(this._qTimer);
      this._qTimer = setTimeout(this.reload, 300); // debounced server-side search
    });
    _defineProperty(this, "openAdd", () => this.setState({
      modalOpen: true
    }));
    _defineProperty(this, "closeModal", () => this.setState({
      modalOpen: false
    }));
    _defineProperty(this, "onSaved", () => {
      this.closeModal();
      this.reload();
    });
    this.state = {
      rows: null,
      count: 0,
      counts: null,
      summary: null,
      legacy: false,
      error: false,
      filter: "",
      channel: "",
      q: urlQ(),
      loadingMore: false,
      modalOpen: false,
      businessModel: null
    };
    this._alive = true;
    this._qTimer = null;
    this._seq = 0;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  async componentDidMount() {
    resolveBusinessModel().then(bm => {
      if (this._alive) this.setState({
        businessModel: bm
      });
    }).catch(() => {});
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
    // The summary strip follows the top-bar dates and comparison.
    this._periodTok = subscribePeriod(() => {
      if (this._alive) this.setState({
        summary: null
      }, () => this.reload());
    });
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._qTimer);
    unsubscribePeriod(this._periodTok);
  }
  query(skip) {
    const {
      filter,
      channel,
      q
    } = this.state;
    const out = {
      top: PAGE,
      skip
    };
    if (filter === "attention") out.attention = true;else if (filter) out.stage = filter;
    if (channel) out.channel = channel;
    if (q.trim()) out.q = q.trim();
    out.range = this.range();
    out.comparison = getGlobalComp() === "prev_year" ? "previous_year" : "previous_period";
    return out;
  }
  legacyQs(skip) {
    const {
      filter,
      channel,
      q
    } = this.state;
    const parts = [`top=${PAGE}`, `skip=${skip}`];
    if (filter === "attention") parts.push(`lapse=${encodeURIComponent("watch,at_risk,lapsing")}`);else if (filter) parts.push(`stage=${filter}`);
    if (channel) parts.push(`channel=${channel}`);
    if (q.trim()) parts.push(`q=${encodeURIComponent(q.trim())}`);
    return parts.join("&");
  }
  async fetchPage(skip) {
    if (!this._legacyMode) {
      const res = await getCustomers(this.query(skip));
      if (res) {
        return {
          rows: (res.items || []).map(normalize),
          count: n(res.count),
          counts: res.counts || null,
          summary: res.summary || null,
          legacy: false
        };
      }
      this._legacyMode = true; // endpoint not deployed yet: stay on the old roster for this visit
    }
    const roster = await fetchSales(`/lifecycle/customers?${this.legacyQs(skip)}`);
    return {
      rows: (roster && roster.rows || []).map(normalizeLegacy),
      count: n(roster && roster.totalCount),
      counts: null,
      summary: null,
      legacy: true
    };
  }
  async load() {
    const seq = ++this._seq;
    try {
      const page = await this.fetchPage(0);
      if (!this._alive || seq !== this._seq) return;
      // Keep the summary from the unfiltered first load when a later response omits it.
      this.setState({
        ...page,
        summary: page.summary || this.state.summary,
        error: false
      });
    } catch (e) {
      if (this._alive && seq === this._seq) this.setState({
        error: true
      });
    }
  }
  renderSummary(nouns) {
    const s = this.state.summary;
    if (!s) return null;
    // s.range = the same numbers over the top-bar dates (newer API); otherwise this month.
    const rg = s.range || null;
    const when = rg ? rangeLabelFor(this.range(), RANGES).toLowerCase() : "this month";
    const vsWord = rg ? getGlobalComp() === "prev_year" ? "vs last year" : "vs the period before" : "vs last month";
    const customers = rg ? n(rg.buyers) : n(s.customers);
    const sales = rg ? n(rg.sales) : n(s.salesMonth);
    const prev = rg ? n(rg.salesPrev) : n(s.salesPrevMonth);
    const orders = rg ? n(rg.orders) : n(s.ordersMonth);
    const fresh = rg ? n(rg.newCustomers) : n(s.newCustomersMonth);
    const attention = n(s.attention);
    if (!customers && !sales && !orders && !fresh && !attention) return null;
    const lead = customers ? `${plural(customers, nouns.one, nouns.many)} ${sales ? `bought ${money(sales, true)} ${when}` : "on the books"}` : sales ? `${money(sales, true)} in sales ${when}` : "";
    const second = [fresh ? `${plural(fresh, `new ${nouns.one}`, `new ${nouns.many}`)}.` : null, attention ? `${count(attention)} usually ${attention === 1 ? "orders" : "order"} by now and ${attention === 1 ? "hasn't" : "haven't"}.` : null].filter(Boolean).join(" ");
    const delta = prev > 0 ? Math.round((sales - prev) / prev * 100) : null;
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-summary"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-summary-text"
    }, lead ? /*#__PURE__*/React.createElement("b", null, lead) : null, second ? /*#__PURE__*/React.createElement("span", null, second) : null), sales || prev ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-stat",
      title: `Sales to ${nouns.many} on file. Sales with no ${nouns.one} record (walk-in or guest orders) are not counted, so this can be lower than total revenue.`
    }, /*#__PURE__*/React.createElement("b", null, money(sales, true)), /*#__PURE__*/React.createElement("span", null, "sales to ", nouns.many, " ", when), delta != null ? /*#__PURE__*/React.createElement("span", {
      className: delta >= 0 ? "pcu-up" : "pcu-down"
    }, delta >= 0 ? "+" : "", delta, "% ", vsWord) : null) : null, orders ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-stat"
    }, /*#__PURE__*/React.createElement("b", null, count(orders)), /*#__PURE__*/React.createElement("span", null, "orders")) : null, /*#__PURE__*/React.createElement("span", {
      className: "pcu-stat"
    }, /*#__PURE__*/React.createElement("b", null, count(attention)), /*#__PURE__*/React.createElement("span", null, "need attention")));
  }
  renderRow(r, nouns) {
    const place = [r.city ? titleCase(r.city) : "", r.state ? String(r.state).toUpperCase() : ""].filter(Boolean).join(", ");
    const sub = [place, r.people != null && n(r.people) > 0 ? plural(r.people, "person", "people") : null].filter(Boolean).join(" · ");
    let last;
    if (r.lastOrder) last = shortDate(r.lastOrder);else if (r.quoteSent || r.lastQuote) last = `Quote sent ${shortDate(r.quoteSent || r.lastQuote)}`;else if (!r.legacy && n(r.orders) === 0) last = "No orders yet";
    return /*#__PURE__*/React.createElement(Link, {
      key: r.id,
      to: `/Customers/account/${encodeURIComponent(r.id)}`,
      className: "pcu-grid pcu-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcu-who"
    }, r.name ? /*#__PURE__*/React.createElement("b", null, r.name) : /*#__PURE__*/React.createElement("span", {
      className: "pcu-noname"
    }, r.email || r.customerNumber || `Unnamed ${nouns.one}`), sub ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-sub"
    }, sub) : null), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(StageChip, {
      stage: r.stage,
      customerWord: nouns.word
    })), /*#__PURE__*/React.createElement("span", {
      className: "pcu-num"
    }, r.orders != null ? count(r.orders) : ""), /*#__PURE__*/React.createElement("span", {
      className: "pcu-num pcu-strong"
    }, r.salesYtd != null ? money(r.salesYtd) : ""), /*#__PURE__*/React.createElement("span", {
      className: "pcu-muted"
    }, last || ""), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(HealthChip, {
      health: r.health
    })));
  }
  render() {
    if (!hasTenantFeature("lifecycle")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "The Customers section is not configured for this workspace. Contact Plunk to enable it."));
    }
    const {
      rows,
      error,
      filter,
      channel,
      q,
      loadingMore,
      counts,
      legacy
    } = this.state;
    const nouns = nounsFor(this.state.businessModel);
    const showDealerPerf = nouns.title === "Dealers" && hasTenantFeature("territoryRep");
    const actions = /*#__PURE__*/React.createElement(React.Fragment, null, showDealerPerf ? /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.props.history.push(`/Dealers/${this.range()}`)
    }, "Dealer performance") : null, /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "150px",
      value: channel,
      onChange: this.onChannel,
      options: CHANNELS
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      onClick: this.openAdd
    }, "Add ", nouns.one));
    const pills = [["", "All", counts && counts.all], ["customer", nouns.title, counts && counts.customer], ["prospect", "Prospects", counts && counts.prospect], ["lead", "Leads", counts && counts.lead], ["attention", "Needs attention", counts && counts.attention]];
    const total = this.state.count;
    let body;
    if (error) body = /*#__PURE__*/React.createElement(Error, null);else if (rows === null) body = /*#__PURE__*/React.createElement(Loader, null);else {
      body = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "pcu-grid pcu-head",
        role: "row"
      }, /*#__PURE__*/React.createElement("span", null, nouns.word), /*#__PURE__*/React.createElement("span", null, "Stage"), /*#__PURE__*/React.createElement("span", {
        className: "pcu-num"
      }, "Orders"), /*#__PURE__*/React.createElement("span", {
        className: "pcu-num"
      }, "Sales this year"), /*#__PURE__*/React.createElement("span", null, "Last order"), /*#__PURE__*/React.createElement("span", null, "Health")), rows.map(r => this.renderRow(r, nouns)), !rows.length ? /*#__PURE__*/React.createElement("div", {
        className: "pcu-empty"
      }, q.trim() ? "Nothing matches that search." : filter === "attention" ? `No ${nouns.many} need attention right now.` : `No ${nouns.many} here yet. They arrive from your customer feed, orders and site activity.`) : null, rows.length < total ? /*#__PURE__*/React.createElement("div", {
        className: "pcu-more"
      }, /*#__PURE__*/React.createElement(Button, {
        onClick: this.loadMore,
        disabled: loadingMore
      }, loadingMore ? "Loading..." : `Show more (${count(total - rows.length)} more)`)) : null);
    }
    return /*#__PURE__*/React.createElement(PeopleShell, {
      cls: "pcu-view",
      customersLabel: nouns.title,
      subtitle: nouns.title === "Dealers" ? PEOPLE_SUBTITLES.dealers : PEOPLE_SUBTITLES.customers,
      actions: actions
    }, this.renderSummary(nouns), /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-toolbar"
    }, pills.map(([v, label, c]) => {
      // Counts come with the rows. On the legacy roster only the selected pill has a true count.
      const shown = c != null ? c : legacy && filter === v && rows ? total : null;
      return /*#__PURE__*/React.createElement("button", {
        key: v || "all",
        type: "button",
        className: `pcu-pill${filter === v ? " on" : ""}`,
        onClick: this.onFilter(v)
      }, label, shown != null ? ` ${count(shown)}` : "");
    }), /*#__PURE__*/React.createElement("span", {
      className: "pcu-flex"
    }), /*#__PURE__*/React.createElement("label", {
      className: "pcu-search"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "search"), /*#__PURE__*/React.createElement("input", {
      type: "search",
      value: q,
      onChange: this.onSearch,
      placeholder: `Name, ${nouns.one} # or city`,
      "aria-label": `Search ${nouns.many}`
    }))), body), this.state.modalOpen && /*#__PURE__*/React.createElement(CustomerModal, {
      customer: null,
      dealers: this.state.businessModel != null && this.state.businessModel !== "retail" && this.state.businessModel !== "both",
      onClose: this.closeModal,
      onSaved: this.onSaved
    }));
  }
}
export default withRouter(CustomersViewComponent);